import { XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef } from 'react';
import { VisibilityIconButton } from '../../components/ui/VisibilityIconButton';
import { DocumentationPageTreeNode, DocumentationVisibility } from '../../services/documentationApi';

export type DocumentationDetailsDraft = {
  visibility: DocumentationVisibility;
  tagInput: string;
  previousPageId: string;
  nextPageId: string;
};

type DocumentationDetailsPanelProps = {
  open: boolean;
  canWrite: boolean;
  draft: DocumentationDetailsDraft;
  pageOptions: DocumentationPageTreeNode[];
  currentPageId: string;
  onClose: () => void;
  onChange: (patch: Partial<DocumentationDetailsDraft>) => void;
};

export function DocumentationDetailsPanel({
  open,
  canWrite,
  draft,
  pageOptions,
  currentPageId,
  onClose,
  onChange,
}: DocumentationDetailsPanelProps) {
  const panelRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!open) return;

    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }

    function onPointer(event: MouseEvent) {
      const target = event.target as Node | null;
      if (panelRef.current && target && !panelRef.current.contains(target)) {
        const toolbarBtn = (event.target as HTMLElement | null)?.closest?.('[aria-label="Detalhes"]');
        if (!toolbarBtn) onClose();
      }
    }

    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open, onClose]);

  if (!open) return null;

  const others = pageOptions.filter((p) => p.id !== currentPageId);
  const isPublic = draft.visibility === 'Public';

  return (
    <aside ref={panelRef} className="docs-details-panel" aria-label="Detalhes da página">
      <div className="docs-details-panel__header">
        <h3 className="docs-details-panel__title">Detalhes</h3>
        <button type="button" className="docs-icon-btn" title="Fechar" aria-label="Fechar" onClick={onClose}>
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="docs-details-panel__body">
        <label className="erp-label-block">
          Visibilidade
          <div className="docs-details-panel__row">
            <VisibilityIconButton
              isPublic={isPublic}
              disabled={!canWrite}
              onClick={() =>
                canWrite &&
                onChange({ visibility: isPublic ? 'Private' : 'Public' })
              }
            />
            <span className="text-sm text-secondary-600">{isPublic ? 'Pública' : 'Privada'}</span>
          </div>
        </label>

        <label className="erp-label-block">
          Tags (separadas por vírgula)
          <input
            className="erp-input"
            value={draft.tagInput}
            disabled={!canWrite}
            onChange={(e) => onChange({ tagInput: e.target.value })}
            placeholder="instalação, api, onboarding"
          />
        </label>

        <label className="erp-label-block">
          Página anterior
          <select
            className="erp-input"
            value={draft.previousPageId}
            disabled={!canWrite}
            onChange={(e) => onChange({ previousPageId: e.target.value })}
          >
            <option value="">— Automática —</option>
            {others.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
              </option>
            ))}
          </select>
        </label>

        <label className="erp-label-block">
          Próxima página
          <select
            className="erp-input"
            value={draft.nextPageId}
            disabled={!canWrite}
            onChange={(e) => onChange({ nextPageId: e.target.value })}
          >
            <option value="">— Automática —</option>
            {others.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
              </option>
            ))}
          </select>
        </label>
      </div>
    </aside>
  );
}
